import React, { useEffect, useState, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { getService } from "../../services/axios/services";
import "./styles/ReleasedTexts.scss";
import ReleasedItemProject from "./ReleasedItemProject/ReleasedItemProject";
import TableLoading from "../../components/TableLoading/TableLoading";
import FilteredReleased from "./FilteredReleased/FilteredReleased";
import CountReleasedAttachments from "./CountReleasedAttachments/CountReleasedAttachments";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import ReleasedFilterFields from "./ReleasedFilterFields/ReleasedFilterFields";
import ContentPagination from "../../components/ContentPagination/ContentPagination";
import NoContents from "../ListStatusActivities/NoContents/NoContents";
import { Link } from "react-router-dom";
import TabsTypesNavigation from "../../components/TabsTypesNavigation/TabsTypesNavigation";
import UserContext from "../../context/userContext";
import { groupFormatsByDramaturgyType } from "../../utils/groupFormatsByDramaturgyType";

const ReleasedTexts = () => {
  const userContext = useContext(UserContext);
  const userChannelIds = userContext?.me?.user?.channels?.map(
    (channel) => channel.id
  );
  const isChannelRole = userContext?.me?.user?.roles?.includes("Channel");

  const [state, setState] = useState({
    loading: true,
    list: {
      items: [],
      totalCount: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPreviousPage: false,
    },
  });

  const [filter, setFilter] = useState({
    origins: [1, 2],
    limit: 12,
    channelIds: [],
    filter: 0,
    idFilter: 0,
    contentType: undefined,
  });
  const [alternativeLoading, setAlternativeLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    primary: "",
    secondary: "",
  });
  const [thesaurusList, setThesaurusList] = useState([]);
  const [dramaturgyFormats, setDramaturgyFormats] = useState([]);
  const [varietyFormats, setVarietyFormats] = useState([]);
  const [stateTabsTypeNavigation, setStateTabsTypeNavigation] = useState({
    loading: true,
    list: [],
  });

  const handlePageChange = (page) => {
    setState({ ...state, loading: true });
    setFilter({ ...filter, pageNumber: page.pageNumber });
  };

  const handleDeleteChannelId = (id) => {
    let channels = filter.channelIds.filter((channel) => channel !== id);
    const { primary, secondary } = selectedFilters;

    const newSelectedFilters = {
      primary: primary !== id ? primary : "",
      secondary: secondary !== id ? secondary : "",
    };

    if (!newSelectedFilters.primary) {
      newSelectedFilters.secondary = "";
      channels = [];
    }

    setFilter((prevState) => ({ ...prevState, channelIds: channels }));
    setSelectedFilters(newSelectedFilters);
  };

  const returnSelectedFilters = () => {
    return thesaurusList.filter((item) => filter.channelIds.includes(item.id));
  };

  const handleChannelIdsFilter = () => {
    const newFilter = { ...filter };

    const isFiltersSelected =
      selectedFilters.primary || selectedFilters.secondary;

    const thesaurusChildren = thesaurusList
      .filter((item) => !!item.parentId)
      .map((item) => item.id);

    const filteredChildrenChannelsIds = userChannelIds?.filter((id) =>
      thesaurusChildren.includes(id)
    );

    if (isChannelRole && !isFiltersSelected) {
      newFilter.channelIds = filteredChildrenChannelsIds;
      newFilter.filter = 0;
      newFilter.idFilter = 0;
    }

    if (isChannelRole && isFiltersSelected) {
      const handledType = selectedFilters.secondary
        ? selectedFilters.secondary
        : selectedFilters.primary;

      const roleIdsAndFilter = filteredChildrenChannelsIds;
      newFilter.channelIds = [...new Set(roleIdsAndFilter)];
      newFilter.filter = 1;
      newFilter.idFilter = handledType;
    }

    return newFilter;
  };

  const getReleaseds = async () => {
    if (!state.loading) {
      setState((prev) => ({ ...prev, loading: true }));
    }

    setAlternativeLoading(true);

    try {
      const handledFilter = handleChannelIdsFilter();

      const res = await getService({
        endpoint: "TextRelease/contents",
        params: {
          Title: handledFilter.title || "",
          PageIndex: handledFilter.pageNumber || 0,
          PageSize: handledFilter.limit || 12,

          ChannelIds:
            handledFilter.channelIds?.length
              ? handledFilter.channelIds
              : undefined,

          FormatIds:
            handledFilter.formatIds?.length
              ? handledFilter.formatIds
              : undefined,

          ContentType: handledFilter.contentType,
        },
        api: "v2",
      });

      setState((prev) => ({
        ...prev,
        loading: false,
        list: res,
      }));
    } catch (error) {
      console.error(error);

      setState((prev) => ({
        ...prev,
        loading: false,
        list: {
          items: [],
        },
      }));
    } finally {
      setAlternativeLoading(false);
    }
  };

  const getDramaturgyFormats = () => {
    return getService({
      endpoint: "dramaturgies/thesaurus?origins=22",
    }).then((res) => {
      setDramaturgyFormats(res.data || []);
    });
  };

  const getVarietyFormats = () => {
    return getService({
      endpoint: "varieties/thesaurus?origins=22",
    }).then((res) => {
      setVarietyFormats(res.data || []);
    });
  };

  const formatGroups = React.useMemo(() => {
    return {
      "Dramaturgia Curta": groupFormatsByDramaturgyType(dramaturgyFormats)["Dramaturgia Curta"],
      "Dramaturgia Longa": groupFormatsByDramaturgyType(dramaturgyFormats)["Dramaturgia Longa"],
      "Variedades": varietyFormats.map((f) => f.id),
    };
  }, [dramaturgyFormats, varietyFormats]);

  const setLoadingTabsTypeNavigation = (bool) => {
    setStateTabsTypeNavigation((prevState) => {
      return {
        ...prevState,
        loading: bool,
      };
    });
  };

  const getTabsTypeNavigation = () => {
    setLoadingTabsTypeNavigation(true);
    getService({ endpoint: "textrelease/groups" }).then((res) => {
      setStateTabsTypeNavigation((prevState) => {
        return {
          ...prevState,
          loading: false,
          list: res.data,
        };
      });
    });
  };

  const getThesaurusList = () => {
    getService({ endpoint: "dramaturgies/thesaurus?origins=28" }).then(
      (res) => {
        const list = res.data.map((item) => ({ ...item, value: item.id }));
        setThesaurusList(list);
      }
    );
  };

  useEffect(() => {
    if (!userContext?.me?.loading && thesaurusList.length) getReleaseds();
    // eslint-disable-next-line
  }, [filter, thesaurusList.length]);

  useEffect(() => {
    getThesaurusList();
    getDramaturgyFormats();
    getVarietyFormats();
    getTabsTypeNavigation();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Helmet>
        <title>Script - Textos Liberados</title>
      </Helmet>
      <div id="ReleasedTexts">
        <Breadcrumb>
          <h2>Textos Liberados</h2>
        </Breadcrumb>

        <div id="TabsTypesNavigation">
          {!stateTabsTypeNavigation.loading && (
            <TabsTypesNavigation
              setFilter={setFilter}
              filter={filter}
              data={stateTabsTypeNavigation.list}
              formatGroups={formatGroups}
            />
          )}
        </div>

        <ReleasedFilterFields
          setFilter={setFilter}
          thesaurusList={thesaurusList}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          isChannelRole={isChannelRole}
        />

        <div className="container">
          <div className="paginationHeader">
            <div className="info">
              <CountReleasedAttachments
                countProjects={state.list?.totalCount}
              />

              {!!filter.channelIds.length && (
                <FilteredReleased
                  selectedFiltersThesaurus={returnSelectedFilters()}
                  handleDeleteChannelId={handleDeleteChannelId}
                />
              )}
            </div>
          </div>
        </div>

        {!state.loading && !state.list?.items.length && <NoContents />}

        {alternativeLoading && <TableLoading />}

        {!state.loading && !!state.list?.items.length && (
          <div className="container contentReleasedAttachments">
            {state.list?.items.map((project, index) => {
              return (
                <ul className="contentListAttachmentsReleased" key={index}>
                  <li>
                    <Link to={`/texto-liberado/${project.id}`}>
                      <ReleasedItemProject project={project} />
                    </Link>
                  </li>
                </ul>
              );
            })}
          </div>
        )}

        {!!state.list?.totalPages && state.list?.totalPages > 1 && (
          <div className="containerPagination">
            <div className="container">
              <ContentPagination
                totalItems={state.list?.totalCount}
                itemsPerPage={state.list?.pageSize}
                currentPage={(state.list?.pageIndex ?? 0) + 1}
                prevPage={
                  state.list?.hasPreviousPage
                    ? state.list.pageIndex
                    : null
                }
                nextPage={
                  state.list?.hasNextPage
                    ? state.list.pageIndex + 2
                    : null
                }
                totalPages={state.list?.totalPages}
                handleChange={(page) =>
                  handlePageChange({
                    pageNumber: page.pageNumber - 1,
                  })
                }
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ReleasedTexts;
